import React, { Fragment, useEffect, useRef, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import QuizIcon from "../../styles/assets/svg/mcqIcon.svg";
import AssignmentIcon from "../../styles/assets/svg/testIcon.svg";
import MenuBookIcon from "../../styles/assets/svg/learnIcon.svg";
import MaterialsIcon from "../../styles/assets/svg/materialsIcon.svg";
import QuizIconActive from "../../styles/assets/svg/mcqIconActive.svg";
import AssignmentIconActive from "../../styles/assets/svg/testIconActive.svg";
import MenuBookIconActive from "../../styles/assets/svg/learnIconActive.svg";
import MaterialsIconActive from "../../styles/assets/svg/materialsIconActive.svg";
import {
  EditIcon,
  DeleteIcon,
  ArrowDropDownIcon,
  ArrowDropUpIcon,
} from "../../icons";
import CreatePagePopupNew from "./CreatePagePopupNew";
import GlobalLoader from "./GlobalLoader";
import DeleteConfirmPopUp from "./deleteConfirmPopUp";
import {
  getPageForCourse,
  getPageForSubject,
  getPageForTopic,
  getPageForSubtopic,
  createPageForCourse,
  createPageForSubject,
  createPageForTopic,
  createPageForSubtopic,
  updateSequenceForCourse,
  updateSequenceForSubject,
  updateSequenceForTopic,
  updateSequenceForSubtopic,
  deletePageForCourse,
  deletePageForSubject,
  deletePageForTopic,
  deletePageForSubtopic,
  updatePageMetadataForCourse,
  updatePageMetadataForSubject,
  updatePageMetadataForTopic,
  updatePageMetadataForSubtopic,
  getPagesForCourse,
  getPagesForSubject,
  getPagesForTopic,
  getPagesForSubtopic,
} from "../../helper/apicalls/course";
import {
  getCourseEvalPageContent,
  getSubjectEvalPageContent,
  getTopicEvalPageContent,
  getSubtopicEvalPageContent,
} from "../../helper/apicalls/eval";
import EvalScoreDetailsPopup from "./EvalScoreDetailsPopup";
import TooltipText from "./tooltipText";
import "./../../styles/common/pagesComponent.scss";
import Cookies from "js-cookie";

const hasValidContent = (val) => {
  if (!val) return false;
  if (Array.isArray(val)) return val.length > 0;
  if (typeof val === "string") {
    const trimmed = val.trim();
    if (
      !trimmed ||
      trimmed === "[]" ||
      trimmed === "null" ||
      trimmed === "undefined"
    )
      return false;
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.length > 0;
      if (parsed && typeof parsed === "object")
        return Object.keys(parsed).length > 0;
    } catch (e) {
      return trimmed.length > 0;
    }
  }
  if (typeof val === "object") return Object.keys(val).length > 0;
  return false;
};

const PagesComponent = ({
  pages = [],
  onAddPage,
  onToggleTest,
  isTestEnabled = false,
  addButtonText = "Add Page",
  courseKey,
  subjectKey,
  topicKey,
  subTopicKey,
  onPageSelect,
  selectedPageData,
  setRemoveCache,
  removeCache,
  readOnly = false,
  evalStatusByPageId = {},
}) => {
  const role = Cookies.get("role");
  const permission = Cookies.get("permissions");
  const permissionData = permission ? JSON.parse(permission) : "";
  const coursePermission =
    permissionData &&
    permissionData?.find((i) => i?.permission_type === "course");

  const controls = useDragControls();
  const [isCreatePagePopupOpen, setIsCreatePagePopupOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState(0);
  const [pagesState, setPagesState] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(null);
  const [activePageId, setActivePageId] = useState(null);
  const [pageCache, setPageCache] = useState({});
  const [openMenuForId, setOpenMenuForId] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [editPageData, setEditPageData] = useState(null);
  const [expandedPages, setExpandedPages] = useState({});
  const [isSubPageMode, setIsSubPageMode] = useState(false);
  const [draggedSequence, setDraggedSequence] = useState(null);
  const [prevReorderableSnapshot, setPrevReorderableSnapshot] = useState(null);
  const [draggedId, setDraggedId] = useState(null);
  const [childDraggedId, setChildDraggedId] = useState("");
  const [childStartDraggedSequence, setStartChildDraggedSequence] =
    useState(null);
  const [childPrevReorderableSnapshot, setChildPrevReorderableSnapshot] =
    useState(null);
  const [childUpdateReorderable, setChildUpdateReorderable] = useState([]);
  const [childParentOrderableId, setChildParentOrderableId] = useState(null);

  const menuRef = useRef(null);

  const [isEvalScorePopupOpen, setIsEvalScorePopupOpen] = useState(false);
  const [selectedEvalOverallScore, setSelectedEvalOverallScore] = useState(null);
  const [selectedEvalScores, setSelectedEvalScores] = useState({});

  const handleChipClick = async (e, pageId, status) => {
    e.stopPropagation();
    if (!status || !status.evaluated) return;

    try {
      setIsLoading(true);
      let payload = null;
      if (courseKey && !subjectKey && !topicKey && !subTopicKey) {
        const res = await getCourseEvalPageContent(status.evaluated_id || pageId);
        payload = res?.data ?? res;
      } else if (subjectKey && !topicKey && !subTopicKey) {
        const res = await getSubjectEvalPageContent(status.evaluated_id || pageId);
        payload = res?.data ?? res;
      } else if (topicKey && !subTopicKey) {
        const res = await getTopicEvalPageContent(status.evaluated_id || pageId);
        payload = res?.data ?? res;
      } else if (subTopicKey) {
        const res = await getSubtopicEvalPageContent(status.evaluated_id || pageId);
        payload = res?.data ?? res;
      }

      if (payload) {
        const overall_score = payload.overall_score ?? payload.overallScore ?? null;
        const evalution_score =
          payload.evalution_score ??
          payload.evalutionScore ??
          payload.evaluation_score ??
          payload.evaluationScore ??
          payload.scores ??
          {};
        setSelectedEvalOverallScore(overall_score);
        setSelectedEvalScores(evalution_score);
        setIsEvalScorePopupOpen(true);
      }
    } catch (err) {
      console.error("Error fetching eval details for popup:", err);
    } finally {
      setIsLoading(false);
    }
  };

  let defaultPageName = "Welcome";
  if (courseKey && !subjectKey && !topicKey && !subTopicKey)
    defaultPageName = "Subjects";
  else if (subjectKey && !topicKey && !subTopicKey) defaultPageName = "Topics";
  else if (topicKey && !subTopicKey) defaultPageName = "Subtopics";
  else if (subTopicKey) defaultPageName = "";

  const pagesWithData = defaultPageName
    ? [
        { key: "default-page", name: defaultPageName, id: "default-page" },
        ...pages,
      ]
    : pages;

  const sortChildPageSquence = (dataArr) => {
    const updateArr = dataArr?.map((data) => {
      if (data?.child_pages?.length > 0) {
        const child = data?.child_pages;
        const sorted = child.sort((a, b) => a.sequence - b.sequence);
        return { ...data, child_pages: sorted };
      }
      return data;
    });
    return updateArr;
  };

  const addDefaultTestSubpages = (dataArr) => {
    if (!Array.isArray(dataArr)) return dataArr;
    return dataArr.map((page, index) => {
      if (
        page?.page_type === "test" &&
        (!page.child_pages || page.child_pages.length === 0)
      ) {
        const baseId = page.id || page.key || `temp-test-${index}`;
        const defaultChildren = [
          {
            id: `${baseId}-easy`,
            name: "Easy",
            page_type: "test",
            sequence: 1,
            is_virtual: true,
            level: "easy",
          },
          {
            id: `${baseId}-medium`,
            name: "Medium",
            page_type: "test",
            sequence: 2,
            is_virtual: true,
            level: "medium",
          },
          {
            id: `${baseId}-hard`,
            name: "Hard",
            page_type: "test",
            sequence: 3,
            is_virtual: true,
            level: "hard",
          },
        ];
        return { ...page, child_pages: defaultChildren };
      }
      return page;
    });
  };

  const currentKey = subTopicKey || topicKey || subjectKey || courseKey;
  const prevKeyRef = useRef(currentKey);

  useEffect(() => {
    const withDefaults = addDefaultTestSubpages(pagesWithData);
    const sorted = sortChildPageSquence(withDefaults);
    setPagesState(sorted);

    const keyChanged = prevKeyRef.current !== currentKey;
    if (keyChanged) {
      prevKeyRef.current = currentKey;
      setPageCache({});
    }

    if (onPageSelect && sorted.length > 0 && (!selectedPageData || keyChanged)) {
      handlePageClick(sorted[0], 0);
    }
  }, [pages, currentKey]);


  const handleOpenCreatePagePopup = () => {
    setEditPageData(null);

    const currentSelectedPage = pagesState[selectedPage];
    if (
      currentSelectedPage &&
      currentSelectedPage.id &&
      (currentSelectedPage.page_type === "quiz" ||
        // currentSelectedPage.page_type === "active_recall" ||
        currentSelectedPage.page_type === "materials")
    ) {
      setIsSubPageMode(true);
    } else {
      setIsSubPageMode(false);
    }

    setIsCreatePagePopupOpen(true);
  };

  const handleCloseCreatePagePopup = () => {
    setIsCreatePagePopupOpen(false);
    setEditPageData(null);
  };

  const handleAddPageClick = () => {
    setEditPageData(null);
    setIsSubPageMode(false);
    setIsCreatePagePopupOpen(true);
  };

  const handleAddSubPageClick = () => {
    const currentSelectedPage = pagesState[selectedPage];
    if (
      !currentSelectedPage ||
      !currentSelectedPage.id ||
      !(
        currentSelectedPage.page_type === "quiz" ||
        // currentSelectedPage.page_type === "active_recall" ||
        currentSelectedPage.page_type === "materials"
      )
    ) {
      return;
    }
    setEditPageData(null);
    setIsSubPageMode(true);
    setIsCreatePagePopupOpen(true);
  };

  const togglePageExpansion = (pageId) => {
    setExpandedPages((prev) => ({
      ...prev,
      [pageId]: !prev[pageId],
    }));
  };

  const handlePageClick = async (page, index, fromChild = false, parentIsAdmin = false, parentPage = null) => {
    const hasChildPages = page.child_pages && page.child_pages.length > 0;
    const isCurrentlyExpanded = !!expandedPages[page.id];
    if (!fromChild && hasChildPages && isCurrentlyExpanded) {
      setExpandedPages((prev) => ({ ...prev, [page.id]: false }));
      return;
    }

    setSelectedPage(index);
    if (!fromChild) {
      setExpandedPages({});
    }
    let targetPage = page;
    let parentForVirtual = null;
    let currentParentPage = null;
    if (hasChildPages) {
      setExpandedPages({ [page.id]: true });

      targetPage = page.child_pages[0];
      currentParentPage = page;
    }
    setActivePageId(idOf(targetPage));

    if (
      targetPage &&
      targetPage.id &&
      (targetPage.page_type === "quiz" || targetPage.page_type === "materials")
    ) {
      setIsSubPageMode(true);
    } else {
      setIsSubPageMode(false);
    }

    let effectivePageId = targetPage.id;
    let levelParam;
    if (targetPage?.is_virtual && targetPage?.page_type === "test") {
      parentForVirtual =
        pagesState.find(
          (p) =>
            Array.isArray(p?.child_pages) &&
            p.child_pages.some((c) => idOf(c) === idOf(targetPage)),
        ) || (page && page.child_pages ? page : null);
      if (parentForVirtual && parentForVirtual.id) {
        effectivePageId = parentForVirtual.id;
      }
      levelParam = targetPage.level || targetPage.name?.toLowerCase();
    }

    if (effectivePageId && onPageSelect) {
      const cacheKey = levelParam
        ? `${effectivePageId}-${levelParam}`
        : effectivePageId;
      const cached = pageCache[cacheKey];
      if (cached) {
        onPageSelect(cached);
        return;
      }

      try {
        setIsLoading(true);
        let fullPageData = null;
        let isAdminPage = false;
        if (page?.id !== "default-page") {
          isAdminPage = !!(
            page?.is_admin_page ||
            targetPage?.is_admin_page ||
            parentForVirtual?.is_admin_page ||
            parentIsAdmin
          );
          if (subTopicKey)
            fullPageData = await getPageForSubtopic(
              subTopicKey,
              effectivePageId,
              levelParam,
              isAdminPage,
            );
          else if (topicKey)
            fullPageData = await getPageForTopic(
              topicKey,
              effectivePageId,
              levelParam,
              isAdminPage,
            );
          else if (subjectKey)
            fullPageData = await getPageForSubject(
              subjectKey,
              effectivePageId,
              levelParam,
              isAdminPage,
            );
          else if (courseKey)
            fullPageData = await getPageForCourse(
              courseKey,
              effectivePageId,
              levelParam,
              isAdminPage,
            );
        }
        let result;
        if (fullPageData) {
          result = levelParam
            ? { ...fullPageData, level: levelParam }
            : fullPageData;
        } else {
          result = targetPage;
        }
        if (result) {
          result.is_admin_page = !!(result.is_admin_page || isAdminPage);
          if (parentPage || currentParentPage) {
            result.parent_page_type = (parentPage || currentParentPage).page_type;
          }
        }
        if (fullPageData && effectivePageId) {
          const keyToCache = levelParam
            ? `${effectivePageId}-${levelParam}`
            : effectivePageId;
          setPageCache((prev) => ({ ...prev, [keyToCache]: result }));
        }

        onPageSelect(result);
      } catch (error) {
        console.error("Error fetching page data:", error);
        if (targetPage && (parentPage || currentParentPage)) {
          targetPage.parent_page_type = (parentPage || currentParentPage).page_type;
        }
        onPageSelect(targetPage);
      } finally {
        setIsLoading(false);
      }
    } else if (onPageSelect) {
      if (targetPage && (parentPage || currentParentPage)) {
        targetPage.parent_page_type = (parentPage || currentParentPage).page_type;
      }
      onPageSelect(targetPage);
    }
  };

  const handleSavePage = async (payload) => {
    try {
      setIsLoading(true);
      let response;

      if (editPageData && editPageData.id) {
        const updatePayload = {
          id: editPageData.id,
          name: payload.name,
          page_type: payload.page_type,
          ...(payload.is_content_quiz_enabled !== undefined
            ? { is_content_quiz_enabled: payload.is_content_quiz_enabled }
            : {}),
          ...(payload.material_type ? { material_type: payload.material_type } : {}),
          ...(payload.page_type === "test"
            ? {
                pass_percentage: payload?.pass_percentage,
                duration: payload?.duration,
              }
            : {}),
        };

        if (subTopicKey) {
          response = await updatePageMetadataForSubtopic(updatePayload);
        } else if (topicKey) {
          response = await updatePageMetadataForTopic(updatePayload);
        } else if (subjectKey) {
          response = await updatePageMetadataForSubject(updatePayload);
        } else if (courseKey) {
          response = await updatePageMetadataForCourse(updatePayload);
        }

        // The pages list endpoint returns metadata only. Keep the fully loaded
        // selected page in the parent so its content is not replaced by a
        // content-less list item after a metadata edit.
        if (selectedPageData?.id === editPageData.id && onPageSelect) {
          onPageSelect({ ...selectedPageData, ...updatePayload });
        }
        await refreshPagesData({ preserveSelection: true });
      } else {
        if (subTopicKey) {
          response = await createPageForSubtopic({
            ...payload,
            subtopic_key: subTopicKey,
          });
        } else if (topicKey) {
          response = await createPageForTopic({
            ...payload,
            topic_key: topicKey,
          });
        } else if (subjectKey) {
          response = await createPageForSubject({
            ...payload,
            subject_key: subjectKey,
          });
        } else if (courseKey) {
          response = await createPageForCourse({
            ...payload,
            course_key: courseKey,
          });
        }

        if (onAddPage) onAddPage(response);

        await refreshPagesData();
      }
    } catch (error) {
      console.error("Error saving page:", error);
    } finally {
      setIsLoading(false);
      setEditPageData(null);
    }
  };

  const callUpdateSequenceApi = async (payload, child, parentId) => {
    if (subTopicKey)
      return updateSequenceForSubtopic(subTopicKey, payload, child, parentId);
    if (topicKey)
      return updateSequenceForTopic(topicKey, payload, child, parentId);
    if (subjectKey)
      return updateSequenceForSubject(subjectKey, payload, child, parentId);
    if (courseKey)
      return updateSequenceForCourse(courseKey, payload, child, parentId);
    return null;
  };

  const refreshPagesData = async ({ preserveSelection = false } = {}) => {
    try {
      let refreshedPages;
      if (subTopicKey) {
        refreshedPages = await getPagesForSubtopic(subTopicKey);
      } else if (topicKey) {
        refreshedPages = await getPagesForTopic(topicKey);
      } else if (subjectKey) {
        refreshedPages = await getPagesForSubject(subjectKey);
      } else if (courseKey) {
        refreshedPages = await getPagesForCourse(courseKey);
      }

      if (refreshedPages && Array.isArray(refreshedPages)) {
        const validPages = refreshedPages.filter((page) => page && page.id);
        let pagesWithDefault = [...validPages];
        if (
          defaultPageName &&
          !validPages.some(
            (p) => p.id === "default-page" || p.key === "default-page",
          )
        ) {
          pagesWithDefault = [
            { key: "default-page", name: defaultPageName, id: "default-page" },
            ...validPages,
          ];
        }

        const withDefaults = addDefaultTestSubpages(pagesWithDefault);
        setPagesState(sortChildPageSquence(withDefaults));
        setPageCache({});

        if (preserveSelection) return;

        if (pagesWithDefault.length > 0) {
          if (selectedPage >= 0 && selectedPage < validPages.length) {
            const selectedPageData = validPages[selectedPage];
            if (
              selectedPageData.child_pages &&
              selectedPageData.child_pages.length > 0
            ) {
              const firstChild = selectedPageData.child_pages[0];
              onPageSelect({
                ...firstChild,
                is_admin_page: !!(firstChild.is_admin_page || selectedPageData.is_admin_page)
              });
            } else {
              onPageSelect(selectedPageData);
            }
          } else if (validPages.length > 0) {
            const firstPage = validPages[0];
            if (firstPage.child_pages && firstPage.child_pages.length > 0) {
              const firstChild = firstPage.child_pages[0];
              onPageSelect({
                ...firstChild,
                is_admin_page: !!(firstChild.is_admin_page || firstPage.is_admin_page)
              });
            } else {
              onPageSelect(firstPage);
            }
            setSelectedPage(0);
          }
        }
      }
    } catch (error) {
      console.error("Error refreshing pages data:", error);
    }
  };

  const idOf = (p) => p.id ?? p.key ?? JSON.stringify(p);

  const handleDragStart = (page) => {
    setDraggedId(idOf(page));
    setDraggedSequence(page.sequence);
    setPrevReorderableSnapshot(
      isSubtopicPage ? pagesState : pagesState.slice(1),
    );
  };

  const handleChildDragStart = (child, parent) => {
    setChildDraggedId(idOf(child));
    setStartChildDraggedSequence(child.sequence);
    const initialList = parent?.child_pages || [];
    setChildPrevReorderableSnapshot(initialList);
    setChildUpdateReorderable(initialList);
    setChildParentOrderableId(parent?.id ?? parent?.key ?? null);
  };

  const handleChildReorder = (childNewOrder, parentId = null) => {
    const targetParentId = parentId ?? childParentOrderableId;
    setChildUpdateReorderable(childNewOrder);
    if (!targetParentId) return;
    setPagesState((prev) => {
      return prev.map((item) => {
        if ((item.id ?? item.key) === targetParentId) {
          return { ...item, child_pages: childNewOrder };
        }
        return item;
      });
    });
  };

  const handleReorder = (newOrder) => {
    if (isSubtopicPage) {
      setPagesState(newOrder);
    } else {
      const updatedPagesState =
        pagesState.length > 0 ? [pagesState[0], ...newOrder] : newOrder;
      setPagesState(updatedPagesState);
    }
  };

  const handleChildDragEnd = async () => {
    const prevChildList = childPrevReorderableSnapshot || [];

    const fromIdxReorderable = prevChildList.findIndex(
      (p) => idOf(p) === childDraggedId,
    );
    const toIdxReorderable = childUpdateReorderable?.findIndex(
      (p) => idOf(p) === childDraggedId,
    );

    const fromSequenceData = prevChildList[fromIdxReorderable]?.sequence;
    const toSquenceData = prevChildList[toIdxReorderable]?.sequence;
    if (
      fromIdxReorderable === -1 ||
      toIdxReorderable === -1 ||
      fromIdxReorderable === toIdxReorderable
    ) {
      setChildDraggedId(null);
      setStartChildDraggedSequence(null);
      setChildPrevReorderableSnapshot(null);
      setChildUpdateReorderable([]);
      setChildParentOrderableId(null);
      return;
    }

    const payload = {
      from_sequence: fromSequenceData,
      to_sequence: toSquenceData,
    };

    try {
      setIsLoading(true);
      await callUpdateSequenceApi(payload, true, childParentOrderableId);

      await refreshPagesData();
    } catch (error) {
      console.error("Error updating child sequence on drop:", error);
    } finally {
      setIsLoading(false);
      setChildDraggedId(null);
      setStartChildDraggedSequence(null);
      setChildPrevReorderableSnapshot(null);
      setChildUpdateReorderable([]);
      setChildParentOrderableId(null);
    }
  };

  const handleDragEnd = async () => {
    const prevReorderable = prevReorderableSnapshot ?? [];
    const currentReorderable = isSubtopicPage
      ? pagesState
      : pagesState.slice(1);

    if (
      !draggedId ||
      draggedSequence == null ||
      !prevReorderable ||
      prevReorderable.length === 0 ||
      !currentReorderable ||
      currentReorderable.length === 0
    ) {
      setDraggedId(null);
      setDraggedSequence(null);
      setPrevReorderableSnapshot(null);
      return;
    }

    const offset = isSubtopicPage ? 0 : 1;

    const fromIdxReorderable = prevReorderable.findIndex(
      (p) => idOf(p) === draggedId,
    );
    const toIdxReorderable = currentReorderable.findIndex(
      (p) => idOf(p) === draggedId,
    );

    if (
      fromIdxReorderable === -1 ||
      toIdxReorderable === -1 ||
      fromIdxReorderable === toIdxReorderable
    ) {
      setDraggedId(null);
      setDraggedSequence(null);
      setPrevReorderableSnapshot(null);
      return;
    }

    const movedItem = currentReorderable[toIdxReorderable];

    const fromIdx = pagesState.findIndex((p) => idOf(p) === draggedId);
    const toIdx = toIdxReorderable + offset;

    const fromSequence =
      fromIdxReorderable >= 0 && fromIdxReorderable < prevReorderable.length
        ? prevReorderable[fromIdxReorderable].sequence
        : draggedSequence;
    const toSequence =
      toIdxReorderable >= 0 && toIdxReorderable < prevReorderable.length
        ? prevReorderable[toIdxReorderable].sequence
        : movedItem.sequence;

    const payload = {
      from_sequence: fromSequence,
      to_sequence: toSequence,
    };

    try {
      setIsLoading(true);
      await callUpdateSequenceApi(payload);

      const reorderedPages = [...pagesState];
      const [removed] = reorderedPages.splice(fromIdx, 1);
      reorderedPages.splice(toIdx, 0, removed);

      const newReorderableWithSeq = reorderedPages
        .slice(offset)
        .map((p, idx) => ({
          ...p,
          sequence: idx + 1,
        }));

      if (isSubtopicPage) {
        setPagesState(newReorderableWithSeq);
      } else {
        setPagesState([reorderedPages[0], ...newReorderableWithSeq]);
      }
      await refreshPagesData();
    } catch (error) {
      console.error("Error updating sequence on drop:", error);
      if (prevReorderableSnapshot) {
        if (isSubtopicPage) {
          setPagesState(prevReorderableSnapshot);
        } else {
          setPagesState([pagesState[0], ...prevReorderableSnapshot]);
        }
      }
    } finally {
      setIsLoading(false);
      setDraggedId(null);
      setDraggedSequence(null);
      setPrevReorderableSnapshot(null);
    }
  };

  const handleMenuOpen = (event, page) => {
    event.stopPropagation();
    setCurrentPage(page);
    const id = page.id ?? page.key ?? null;
    setOpenMenuForId(id);
  };

  const handleMenuClose = () => {
    setOpenMenuForId(null);
    setCurrentPage(null);
  };

  const handleEdit = async () => {
    setIsCreatePagePopupOpen(true);
    if (!currentPage || !currentPage.id) {
      console.warn("Cannot edit page without ID");
      return;
    }
    let parentId = null;
    let parentType = null;
    try {
      const parent = pagesState.find(
        (p) =>
          Array.isArray(p?.child_pages) &&
          p.child_pages.some((c) => idOf(c) === idOf(currentPage)),
      );
      parentType = parent?.page_type;
      parentId = parent?.id;
      if (parentType === "quiz" || parentType === "materials") {
        setIsSubPageMode(true);
      }
    } catch (_) {}

    setEditPageData({
      id: currentPage.id,
      name: currentPage.name,
      page_type: currentPage.page_type || "content",
      material_type: currentPage.material_type || null,
      duration: currentPage?.duration || 0,
      pass_percentage: currentPage?.pass_percentage || 0,
      is_content_quiz_enabled:
        currentPage?.is_content_quiz_enabled !== undefined
          ? currentPage?.is_content_quiz_enabled
          : true,
      parent_id: parentId,
      parent_page_type: parentType,
    });

    setIsCreatePagePopupOpen(true);
  };

  const handleDelete = () => {
    setIsConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async (e) => {
    if (e && typeof e.stopPropagation === "function") {
      e.stopPropagation();
    }

    const pageToDelete = currentPage;

    setIsConfirmDeleteOpen(false);
    handleMenuClose();

    if (!pageToDelete || !pageToDelete.id) {
      console.warn("Cannot delete page without ID");
      return;
    }

    try {
      setIsLoading(true);
      setIsConfirmDeleteOpen(false);

      if (subTopicKey) {
        await deletePageForSubtopic(pageToDelete.id);
      } else if (topicKey) {
        await deletePageForTopic(pageToDelete.id);
      } else if (subjectKey) {
        await deletePageForSubject(pageToDelete.id);
      } else if (courseKey) {
        await deletePageForCourse(pageToDelete.id);
      }

      await refreshPagesData();

      console.log("Page deleted successfully");
    } catch (error) {
      console.error("Error deleting page:", error);
      alert("Failed to delete page. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setIsConfirmDeleteOpen(false);
  };
  useEffect(() => {
    if (removeCache && selectedPageData) {
      const level = selectedPageData.level;
      const cacheKey = level
        ? `${selectedPageData.id}-${level}`
        : selectedPageData.id;

      setPageCache((prev) => ({
        ...prev,
        [cacheKey]: selectedPageData,
      }));
      setRemoveCache(false);
    }
  }, [removeCache, selectedPageData]);

  const isSubtopicPage = !!subTopicKey;
  const fixedItem = pagesState.length > 0 ? pagesState[0] : null;

  const reorderableItems = isSubtopicPage
    ? pagesState
    : pagesState.length > 1
      ? pagesState.slice(1)
      : [];
  const fixedItems = isSubtopicPage
    ? []
    : pagesState.length > 0
      ? [pagesState[0]]
      : [];

  const currentSelectedPage = pagesState[selectedPage];
  const canShowAddSubPageButton =
    currentSelectedPage &&
    currentSelectedPage.id &&
    (currentSelectedPage.page_type === "quiz" ||
      // currentSelectedPage.page_type === "active_recall" ||
      currentSelectedPage.page_type === "materials");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuForId("");
      }
    };

    if (openMenuForId) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuForId]);

  return (
    <div className="pages-component-container">
      {isLoading && <GlobalLoader visible={isLoading} text="Updating..." />}

      <div className="pages-component-paper">
        <div className="pages-list-container">
          {!isSubtopicPage && fixedItem && (
            <div
              className={`page-list-item ${
                selectedPage === 0 ? "selected" : ""
              }`}
              onClick={() => handlePageClick(fixedItem, 0)}
            >
              <div
                className={`page-list-item-text ${
                  selectedPage === 0 ? "selected" : ""
                }`}
              >
                <span className="page-name">
                  <TooltipText text={fixedItem.name} fullWidth />
                </span>
              </div>
            </div>
          )}

          <Reorder.Group
            axis="y"
            values={reorderableItems}
            onReorder={handleReorder}
            className="pages-list"
          >
            {reorderableItems.map((page, idx) => {
              const actualIndex = isSubtopicPage ? idx : idx + 1;
              const key = page.id ?? page.key ?? `page-${actualIndex}`;

              const hasChildPages =
                page.child_pages && page.child_pages.length > 0;
              const isExpanded = expandedPages[page.id];

              let PageIcon;
              const pageType = page.page_type || "content";
              const isSelected = selectedPage === actualIndex;
              const isAdminPageItem = !!page?.is_admin_page;
              const canShowPageActions =
                !readOnly &&
                (role === "admin" ||
                  (!isAdminPageItem && coursePermission?.write));

              if (pageType === "quiz") {
                PageIcon = isSelected ? QuizIconActive : QuizIcon;
              } else if (pageType === "test") {
                PageIcon = isSelected ? AssignmentIconActive : AssignmentIcon;
              } else if (pageType === "content") {
                PageIcon = isSelected ? MenuBookIconActive : MenuBookIcon;
              } else if (pageType === "materials") {
                PageIcon = isSelected ? MaterialsIconActive : MaterialsIcon;
              } else {
                PageIcon = isSelected ? MenuBookIconActive : MenuBookIcon;
              }

              return (
                <Fragment key={key}>
                  <Reorder.Item
                    value={page}
                    id={key}
                    dragControls={controls}
                    dragListener={canShowPageActions}
                    className={`page-list-item ${
                      selectedPage === actualIndex ? "selected" : ""
                    } `}
                    onClick={() => handlePageClick(page, actualIndex)}
                    onDragStart={() => handleDragStart(page)}
                    onDragEnd={() => handleDragEnd()}
                    whileDrag={{ scale: 1.02 }}
                  >
                    <div
                      className={`page-list-item-text ${
                        selectedPage === actualIndex ? "selected" : ""
                      }`}
                    >
                      {PageIcon && (
                        <img
                          src={PageIcon}
                          alt={pageType}
                          className="page-type-icon"
                          draggable={false}
                        />
                      )}
                      <span className="page-name">
                        <TooltipText text={page.name} fullWidth />
                      </span>
                      {(() => {
                        const pageId = page.id || page.key;
                        if (pageId === "default-page") return null;
                        const status = evalStatusByPageId[pageId];
                        // For test pages: show partial score even if not all levels evaluated
                        const isTestPage = page.page_type === "test";
                        const hasScore = status && status.overall_score !== null && typeof status.overall_score !== "undefined";
                        const shouldShow = hasScore && (isTestPage ? true : status.evaluated);
                        if (shouldShow) {
                          return (
                            <span
                              className="eval-score-chip"
                              onClick={(e) => handleChipClick(e, pageId, status)}
                              style={{ cursor: "pointer" }}
                            >
                              {status.overall_score + " "}/ 10
                            </span>
                          );
                        }
                        return null;
                      })()}
                      {page?.is_admin_page && (
                        <span className="admin-default-chip">Default</span>
                      )}
                      {hasChildPages && (
                        <span
                          className="expand-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isExpanded) {
                              handlePageClick(page, actualIndex);
                            } else {
                              togglePageExpansion(page.id);
                            }
                          }}
                        >
                          {isExpanded ? (
                            <ArrowDropUpIcon />
                          ) : (
                            <ArrowDropDownIcon />
                          )}
                        </span>
                      )}
                    </div>
                    {canShowPageActions && (
                      <div className="page-actions">
                        <MoreVertIcon
                          className="more-vert-icon"
                          onClick={(e) => handleMenuOpen(e, page)}
                        />
                        <DragIndicatorIcon className="drag-indicator-icon" />
                        {openMenuForId === (page.id ?? page.key ?? null) && (
                          <div
                            className="page-menu"
                            onClick={(e) => e.stopPropagation()}
                            ref={menuRef}
                          >
                            <button
                              type="button"
                              className="page-menu-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit();
                              }}
                            >
                              <EditIcon className="page-menu-icon" />
                              <span>Edit</span>
                            </button>
                            <button
                              type="button"
                              className="page-menu-item danger"
                              onClick={handleDelete}
                            >
                              <DeleteIcon className="page-menu-icon" />
                              <span>Delete</span>
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </Reorder.Item>

                  {hasChildPages && isExpanded && (
                    <div className="child-pages-container">
                      <Reorder.Group
                        axis="y"
                        values={page?.child_pages}
                        onReorder={(newOrder) =>
                          handleChildReorder(
                            newOrder,
                            page.id ?? page.key ?? null,
                          )
                        }
                        className="pages-list"
                      >
                        {page.child_pages.map((childPage, childIdx) => {
                          const childKey = childPage.id ?? `child-${childIdx}`;
                          const childPageType =
                            childPage.page_type || "content";
                          const isChildActive =
                            activePageId === idOf(childPage);
                          const isTestParent = page.page_type === "test";
                          const isSameParentAsSelected =
                            selectedPageData && selectedPageData.id === page.id;
                          const easyArray =
                            isSameParentAsSelected &&
                            Array.isArray(selectedPageData?.content)
                              ? selectedPageData.content
                              : [];
                          const mediumArray =
                            isSameParentAsSelected &&
                            Array.isArray(selectedPageData?.content)
                              ? selectedPageData.content
                              : [];
                          const easyHasContent = easyArray.length > 0;
                          const mediumHasContent = mediumArray.length > 0;
                          const childLevel = (
                            childPage.level ||
                            childPage.name ||
                            ""
                          ).toLowerCase();

                          let isChildDisabled = false;
                          if (isTestParent) {
                            if (selectedPageData?.level === "easy") {
                              if (childLevel === "medium") {
                                isChildDisabled = !easyHasContent;
                              }
                              if (childLevel === "hard") {                                
                                isChildDisabled = !(
                                  easyHasContent && mediumHasContent
                                );
                              }
                            } else if (selectedPageData?.level === "medium") {
                              if (childLevel === "hard") {
                                isChildDisabled = !mediumHasContent;
                              }
                            }
                          }
                          let ChildPageIcon;
                          const isAdminChildItem = !!(
                            childPage?.is_admin_page || page?.is_admin_page
                          );
                          const canShowChildActions =
                            !readOnly &&
                            !isTestParent &&
                            (role === "admin" ||
                              (!isAdminChildItem && coursePermission?.write));

                          if (childPageType === "quiz") {
                            ChildPageIcon = isChildActive
                              ? QuizIconActive
                              : QuizIcon;
                          } else if (childPageType === "test") {
                            ChildPageIcon = isChildActive
                              ? AssignmentIconActive
                              : AssignmentIcon;
                          } else if (childPageType === "content") {
                            ChildPageIcon = isChildActive
                              ? MenuBookIconActive
                              : MenuBookIcon;
                          } else if (childPageType === "materials") {
                            ChildPageIcon = isChildActive
                              ? MaterialsIconActive
                              : MaterialsIcon;
                          } else {
                            ChildPageIcon = isChildActive
                              ? MenuBookIconActive
                              : MenuBookIcon;
                          }

                          return (
                            <Fragment key={childKey}>
                              <Reorder.Item
                                value={childPage}
                                id={childKey}
                                className={`child-page-item ${
                                  activePageId === idOf(childPage)
                                    ? "selected"
                                    : ""
                                } ${isChildDisabled ? "disabled" : ""}`}
                                dragControls={controls}
                                dragListener={canShowChildActions}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isChildDisabled) return;
                                  handlePageClick(childPage, actualIndex, true, !!page?.is_admin_page, page);
                                }}
                                onDragStart={() => {
                                  handleChildDragStart(childPage, page);
                                }}
                                onDragEnd={() => handleChildDragEnd()}
                                whileDrag={{ scale: 1.02 }}
                              >
                                <div className="child-page-item-text">
                                  {ChildPageIcon && (
                                    <img
                                      src={ChildPageIcon}
                                      alt={childPageType}
                                      className="page-type-icon"
                                      draggable={false}
                                    />
                                  )}
                                  <span className="page-name">
                                    <TooltipText text={childPage.name} fullWidth />
                                  </span>
                                  {(() => {
                                    const status =
                                      evalStatusByPageId[childPage.id];
                                    if (
                                      status &&
                                      status.evaluated &&
                                      status.overall_score !== null
                                    ) {
                                      return (
                                        <span
                                          className="eval-score-chip"
                                          onClick={(e) => handleChipClick(e, childPage.id, status)}
                                          style={{ cursor: "pointer" }}
                                        >
                                          {status.overall_score + " "}/ 10
                                        </span>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>
                                {canShowChildActions && (
                                  <div className="page-actions">
                                    <MoreVertIcon
                                      className="more-vert-icon"
                                      onClick={(e) =>
                                        handleMenuOpen(e, childPage)
                                      }
                                    />
                                    <DragIndicatorIcon className="drag-indicator-icon" />
                                    {openMenuForId ===
                                      (childPage.id ??
                                        childPage.key ??
                                        null) && (
                                      <div
                                        ref={menuRef}
                                        className="page-menu"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <button
                                          type="button"
                                          className="page-menu-item"
                                          onClick={handleEdit}
                                        >
                                          <EditIcon className="page-menu-icon" />
                                          <span>Edit</span>
                                        </button>
                                        <button
                                          type="button"
                                          className="page-menu-item danger"
                                          onClick={handleDelete}
                                        >
                                          <DeleteIcon className="page-menu-icon" />
                                          <span>Delete</span>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </Reorder.Item>
                            </Fragment>
                          );
                        })}
                      </Reorder.Group>
                    </div>
                  )}

                  {actualIndex < pagesState.length - 1 && (
                    <div className="page-divider" />
                  )}
                </Fragment>
              );
            })}

            {reorderableItems.length === 0 && (
              <div className="no-pages-container">
                <div className="no-pages-text">No items created yet.</div>
              </div>
            )}
          </Reorder.Group>
        </div>

        {!readOnly && (role === "admin" || coursePermission?.write) && (
          <div className="pages-actions-container">
            <button className="add-page-button" onClick={handleAddPageClick}>
              <AddCircleOutlineIcon className="add-page-icon" />
              <span>{addButtonText}</span>
            </button>
            {canShowAddSubPageButton && (
              <button
                className="add-page-button"
                onClick={handleAddSubPageClick}
              >
                <AddCircleOutlineIcon className="add-page-icon" />
                <span>Add Sub Page</span>
              </button>
            )}
          </div>
        )}
      </div>

      {isCreatePagePopupOpen && (
        <CreatePagePopupNew
          open={isCreatePagePopupOpen}
          handleClose={handleCloseCreatePagePopup}
          onSave={handleSavePage}
          courseKey={courseKey}
          subjectKey={subjectKey}
          topicKey={topicKey}
          subTopicKey={subTopicKey}
          editData={editPageData}
          isSubPage={isSubPageMode}
          parentId={
            editPageData?.parent_id ||
            (isSubPageMode && currentSelectedPage && currentSelectedPage.id
              ? currentSelectedPage.id
              : null)
          }
          parentPageType={
            editPageData?.parent_page_type ||
            (isSubPageMode &&
            currentSelectedPage &&
            currentSelectedPage.page_type
              ? currentSelectedPage.page_type
              : null)
          }
        />
      )}

      {isConfirmDeleteOpen && (
        <DeleteConfirmPopUp
          isConfirmDeleteOpen={isConfirmDeleteOpen}
          setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
          handleDelete={handleConfirmDelete}
          title="Delete Page"
          message="Are you sure you want to delete this page? This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
        />
      )}

      <EvalScoreDetailsPopup
        open={isEvalScorePopupOpen}
        onClose={() => setIsEvalScorePopupOpen(false)}
        overall={selectedEvalOverallScore}
        scores={selectedEvalScores}
      />
    </div>
  );
};

export default PagesComponent;
